import React from 'react';
import { Badge } from '@parsina/framework';

import { useTranslation, LTR_TEXT, toAsciiDigits } from '../../i18n';
import { EMPTY } from '../../lib/format';

/**
 * The vocabulary of «یکی کردن حساب‌ها» (PM-0536 P5b, PM-0546), in one place.
 *
 * The server is the authority on every rule here: it recomputes the refusals on
 * the preview AND on the write. What this file mirrors it does so only to decide
 * what to OFFER — which «این حساب بماند» is pressable — so an operator is told
 * why before they ask, not after.
 */

/** Mirrors the admin BFF's REASON_MIN / REASON_MAX (resolvers/accounts.js). */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

/** `consolidatePatientAccounts.result`, a closed set; anything else is echoed raw. */
export const RESULTS = ['merged', 'moved', 'none'];

/** `patientIdAttribute.state`, a closed set; anything else is echoed raw. */
export const ATTRIBUTE_STATES = ['HERE', 'MERGED', 'ABSENT'];

/** The Keycloak steps of a consolidation, in the order the BFF runs them. */
export const KEYCLOAK_STEPS = ['clear_phone', 'disable', 'logout', 'patient_id'];

/**
 * Why `account` may never be the one dropped — the DROP half of the BFF's
 * `consolidationRefusals`, same codes. A doctor's or a staff member's account
 * always survives; an unanswered practitioner lookup (null, never 0) refuses too.
 */
export function dropBlockers(account) {
  if (!account) return [];
  const codes = [];
  if (account.nonPatientRoles?.length) codes.push('DROP_HAS_ROLES');
  if (account.groups?.length) codes.push('DROP_IN_GROUP');
  if (account.clinicId) codes.push('DROP_HAS_CLINIC');
  if (account.practitionerClinics == null) codes.push('PRACTITIONER_UNKNOWN');
  else if (account.practitionerClinics > 0) codes.push('DROP_IS_PRACTITIONER');
  return codes;
}

/**
 * Which of two chosen accounts to keep first: the one that may not be dropped,
 * else the enabled one (a disabled kept account is refused), else the first
 * chosen. The operator can swap it whenever the swap is allowed.
 */
export function initialOrientation(first, second) {
  const firstBlocked = dropBlockers(first).length > 0;
  const secondBlocked = dropBlockers(second).length > 0;
  if (secondBlocked && !firstBlocked) return { keepId: second.id, dropId: first.id };
  if (!firstBlocked && !secondBlocked && !first.enabled && second.enabled) {
    return { keepId: second.id, dropId: first.id };
  }
  return { keepId: first.id, dropId: second.id };
}

/**
 * The records the lookup returned, once each: those carrying the searched کد ملی
 * and those each account holds here. An account holds at most one; two accounts
 * never hold the same one, but the two lists overlap.
 */
export function recordsOf(lookup) {
  const byId = new Map();
  for (const r of lookup?.records || []) byId.set(r.id, r);
  for (const a of lookup?.accounts || []) for (const r of a.records || []) if (!byId.has(r.id)) byId.set(r.id, r);
  return [...byId.values()];
}

/** The search's variables: ASCII digits, trimmed, and an absent field left out. */
export function searchVariables({ phone, nationalId } = {}) {
  const p = toAsciiDigits(String(phone ?? '')).trim();
  const n = toAsciiDigits(String(nationalId ?? '')).trim();
  return { phone: p || null, nationalId: n || null };
}

/** A person's name as the account holds it, else its username, else its id. */
export function accountName(account) {
  if (!account) return '';
  const name = [account.firstName, account.lastName].filter(Boolean).join(' ').trim();
  return name || account.username || account.id;
}

/** An identifier (number, username, id): an LTR island, never translated. */
export function Ident({ value, className = '', 'data-testid': testId }) {
  if (!value) return <span data-testid={testId}>{EMPTY}</span>;
  return (
    <span {...LTR_TEXT} className={`font-mono text-xs ${className}`} data-testid={testId}>{value}</span>
  );
}

export function EnabledBadge({ enabled, 'data-testid': testId }) {
  const { t } = useTranslation();
  return (
    <Badge variant={enabled ? 'success' : 'outline'} data-testid={testId}>
      {enabled ? t('accounts.account.enabled') : t('accounts.account.disabled')}
    </Badge>
  );
}

const ATTRIBUTE_TONE = { HERE: 'success', MERGED: 'warning', ABSENT: 'outline' };

/** Where an account's patient_id attribute points in THIS database. */
export function AttributeBadge({ attribute, 'data-testid': testId }) {
  const { t, has } = useTranslation();
  if (!attribute) return <span data-testid={testId}>{t('accounts.attribute.none')}</span>;
  const key = `accounts.attribute.state.${attribute.state}`;
  return (
    <Badge
      variant={ATTRIBUTE_TONE[attribute.state] || 'outline'}
      title={has && has(`accounts.attribute.hint.${attribute.state}`) ? t(`accounts.attribute.hint.${attribute.state}`) : undefined}
      data-testid={testId}
    >
      {has && has(key) ? t(key) : String(attribute.state)}
    </Badge>
  );
}

/** A refusal code in words, else the server's own sentence. */
export function refusalText(t, has, refusal) {
  const key = `accounts.refusals.${refusal.code}`;
  return has && has(key) ? t(key) : (refusal.message || refusal.code);
}
